import dayjs from 'dayjs';
import type { SubscriptionRequestDTO } from '../subscriptionRequest.types';
import i18n from '@locales/i18n';
import { SubscriptionRequestHistoryDTOModel } from './SubscriptionRequestHistoryDTOModel';

export class SubscriptionRequestModel {
  private readonly dto: SubscriptionRequestDTO;
  private readonly _histories: SubscriptionRequestHistoryDTOModel[] | [];
  private formatDate(date: string, format: string): string {
    const formatted = dayjs(date).format(format);
    return formatted === 'Invalid Date' ? '-' : formatted;
  }
  constructor(dto?: SubscriptionRequestDTO) {
    this.dto = dto ?? {
      id: -1,
      tenant: { id: '', name: '', tenantCode: '' },
      software: {
        id: '',
        name: '',
        productCode: '',
        planVisibilityScope: 'ALL_USERS',
      },
      user: {
        id: '',
        email: '',
        username: '',
        name: '',
        phone: '',
        status: 'CREATED',
      },
      manager: { id: '', email: '', username: '', name: '', phone: '' },
      requester: { name: '' },
      status: 'REQUESTED',
      inquiry: '',
      createdDate: '',
      startDate: '',
      endDate: '',
      timeZone: '',
      productPlan: {},
      histories: [],
      type: 'EXTERNAL',
    };
    this._histories = dto?.histories?.map(history => new SubscriptionRequestHistoryDTOModel(history)) ?? [];
  }

  get isValidModel() {
    return this.id !== -1;
  }

  get productPlanId() {
    return this.dto.productPlan.planId ?? '';
  }

  get productPlanName() {
    return this.dto.productPlan.planName ?? '-';
  }

  hasProductPlanName() {
    return !!this.dto.productPlan.planName;
  }

  get id() {
    return this.dto.id;
  }

  get tenant() {
    return this.dto.tenant;
  }

  get software() {
    return this.dto.software;
  }

  get inquiry() {
    return this.dto.inquiry;
  }

  get requesterName() {
    return this.dto.user.name || '-';
  }

  get managerId() {
    return this.dto.manager?.id ?? '';
  }

  get managerName() {
    return this.dto.manager?.name || '-';
  }

  get user() {
    return this.dto.user;
  }

  get startDate() {
    return this.dto.startDate;
  }

  get endDate() {
    return this.dto.endDate;
  }

  hasCreatedDate() {
    return !!this.dto.createdDate;
  }

  hasSubscriptionId() {
    return this.dto.id === -1;
  }

  get formattedCreatedDateTime() {
    return this.formatDate(this.dto.createdDate, 'YYYY.MM.DD HH:mm');
  }

  get formattedCreatedDate() {
    return this.formatDate(this.dto.createdDate, 'YYYY.MM.DD');
  }

  get formattedStartDate() {
    return this.formatDate(this.dto.startDate, 'YYYY.MM.DD');
  }

  get formattedEndDate() {
    return this.formatDate(this.dto.endDate ?? '', 'YYYY.MM.DD');
  }

  get usagePeriod() {
    if (this.formattedStartDate === '-' && this.formattedEndDate === '-') {
      return '-';
    }
    if (this.formattedStartDate === '-') {
      return `~ ${this.formattedEndDate}`;
    }
    if (this.formattedEndDate === '-') {
      return `${this.formattedStartDate} ~`;
    }
    return `${this.formattedStartDate} ~ ${this.formattedEndDate}`;
  }

  get status() {
    return this.dto.status;
  }

  get timeZone() {
    return this.dto.timeZone;
  }

  get statusStr() {
    return this.getStatusStr(this.status);
  }

  // 요청자
  get requester() {
    return this.dto.requester;
  }

  getStatusStr(status: string) {
    switch (status) {
      case 'APPROVED':
        return i18n.t('Subscrib_Detail_BuyRequest_18');
      case 'CANCELLED':
        return i18n.t('Subscrib_Detail_BuyRequest_13');
      case 'DELIVERED':
        return i18n.t('Subscrib_Detail_BuyRequest_17');
      case 'QUOTATION':
        return i18n.t('Subscrib_Detail_BuyRequest_20');
      case 'REJECTED':
        return i18n.t('Subscrib_Detail_BuyRequest_19');
      case 'REQUESTED':
        return i18n.t('Subscrib_Detail_BuyRequest_16');
      default:
        return i18n.t('Subscrib_Detail_BuyRequest_16');
    }
  }

  get statusLabelColor() {
    return this.getStatusLabelColor(this.status);
  }

  getStatusLabelColor(status: string) {
    switch (status) {
      case 'APPROVED':
        return 'pink';
      case 'REJECTED':
        return 'gray';
      case 'REQUESTED':
        return 'blue';
      case 'QUOTATION':
        return 'yellow';
      case 'CANCELLED':
        return 'red';
      case 'DELIVERED':
        return 'green';
      default:
        return 'blue';
    }
  }

  get histories(): SubscriptionRequestHistoryDTOModel[] | [] {
    return this._histories.sort((a, b) => (a.createdDate > b.createdDate ? -1 : 1));
  }

  getHistories(excludesFirst: boolean) {
    if (this.dto.histories.length === 1) {
      return this.dto.histories.map(history => new SubscriptionRequestHistoryDTOModel(history));
    }
    return this.dto.histories
      .map(history => new SubscriptionRequestHistoryDTOModel(history))
      .filter(history => (excludesFirst ? history.status !== 'REQUESTED' : true))
      .sort((a, b) => dayjs(b.createdDate).diff(dayjs(a.createdDate)));
  }
}
